
import { FleetNavigationModule } from "@app/main/modules/fleet/fleet.navigation";
import { AccountService } from "@services/account.service";
import { AccountOption } from "@services/class/AccountOption";
import { SessionService } from "@services/session.service";
import { StateService } from "@uirouter/angularjs";
import * as angular from "angular";
import { SettingsName } from "../../class/SettingsName";
import { AccountFeatures } from "../../enum/account/AccountFeature";
import { AccountFeatureService, FeatureAccessType } from "../../services/accountFeature.service";

export class IfEnabled implements angular.IDirective {

    restrict = "A";
    terminal = true;
    priority = 1000;

    link: (scope: angular.IScope, element: ng.IAugmentedJQuery, attrs: ng.IAttributes) => void;

    constructor(
        private readonly $compile: angular.ICompileService,
        private readonly $state: StateService,
        private readonly accountService: AccountService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly sessionService: SessionService
    ) {
        IfEnabled.prototype.link = ($scope: angular.IScope, $element: angular.IAugmentedJQuery, $attrs: ng.IAttributes) => {
            const readFeature = $attrs["ifFeatureReadAccess"] as string;
            const manageFeature = $attrs["ifFeatureManageAccess"] as string;

            const roles = $attrs["ifUserRole"] as string;
            const option = $attrs["ifAccountOption"] as string;

            if (!readFeature && !manageFeature && !roles && !option) {
                return;
            }

            const allRoles = $attrs["allRoles"] as string;
            let requireAllRoles = false;

            if (allRoles && allRoles.toLowerCase() === "true") {
                requireAllRoles = true;
            }

            const redirectAttr = $attrs["redirect"] as string;

            let redirect = false;
            if (redirectAttr && redirectAttr.toLowerCase() === "true") {
                redirect = true;
            }

            let removeElement = false;

            if (this.removeDueToFeatureAccess(readFeature, manageFeature) || this.removeDueToUserRole(roles, option, requireAllRoles)) {
                removeElement = true;
            }

            if (removeElement && redirect) {
                this.$state.go(FleetNavigationModule.Default);
            }
            else if (removeElement) {
                setTimeout(() => {
                    $element.remove();
                });
            }
            else {
                $element.removeAttr("if-enabled");
                $element.removeAttr("if-feature-read-access"); // previously if-account-feature
                $element.removeAttr("if-feature-manage-access");
                $element.removeAttr("if-user-role");
                $element.removeAttr("if-account-option");
                $element.removeAttr("all-roles");
                $element.removeAttr("redirect");
                $element.removeAttr("account-id");

                this.$compile($element)($scope);
            }
        }

    }

    removeDueToFeatureAccess(readFeature: string, manageFeature: string): boolean {
        if (!readFeature && !manageFeature) {
            return false
        }

        const accountData = this.accountService.getAccountDataFromCache();
        const accountFeatureDescriptions = this.accountFeatureService.getFeatureDescriptionsForAccount(accountData);

        let hasReadFeatureAccess = true;
        let hasManageFeatureAccess = true;

        if (readFeature) {
            const matchingReadFeature = accountFeatureDescriptions.find(f => AccountFeatures[f.feature].toLowerCase() === readFeature.toLowerCase());

            if (matchingReadFeature) {
                hasReadFeatureAccess = this.accountFeatureService.currentUserHasFeatureAccess(matchingReadFeature.feature, FeatureAccessType.Read);
            }
        }

        if (manageFeature) {
            const matchingManageFeature = accountFeatureDescriptions.find(f => AccountFeatures[f.feature].toLowerCase() === manageFeature.toLowerCase());

            if (matchingManageFeature) {
                hasManageFeatureAccess = this.accountFeatureService.currentUserHasFeatureAccess(matchingManageFeature.feature, FeatureAccessType.Manage);
            }
        }

        return !hasReadFeatureAccess || !hasManageFeatureAccess;
    }

    removeDueToUserRole(roles: string, option: string, requireAllRoles: boolean): boolean {
        if (!roles && !option) {
            return false;
        }

        let removeElement = false;

        if (roles) {
            if (!this.accountFeatureService.currentUserHasRole(roles.split(","), requireAllRoles)) {
                removeElement = true;
            }
        }

        if (option) {
            const accountOptions = this.sessionService.getItem<AccountOption>(SettingsName.AccountOptions);

            if (!accountOptions || !accountOptions[option]) {
                removeElement = true;
            }
        }

        return removeElement;
    }

    static factory(): angular.IDirectiveFactory {
        const directive = ($compile: angular.ICompileService, $state: StateService, accountService: AccountService, accountFeatureService: AccountFeatureService, sessionService: SessionService) => new IfEnabled($compile, $state, accountService, accountFeatureService, sessionService);

        directive.$inject = ["$compile", "$state", "accountService", "accountFeatureService", "sessionService", "foundationAccountService"];

        return directive;
    }
}
